import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';

export const PatchNotesPage: React.FC = () => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [patchNotesFiles, setPatchNotesFiles] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Parse date from filename (MMDDYYYY.md format)
  const parseDateFromFilename = (filename: string): Date | null => {
    const match = filename.match(/^(\d{2})(\d{2})(\d{4})\.md$/);
    if (match && match[1] && match[2] && match[3]) {
      const month = match[1];
      const day = match[2];
      const year = match[3];
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    return null;
  };

  // Discover patch notes files by trying date patterns (MMDDYYYY.md format)
  const discoverPatchNotesFiles = async (): Promise<string[]> => {
    const currentDate = new Date();
    const startDate = new Date(currentDate.getFullYear() - 2, 0, 1); // 2 years ago
    
    // Generate date patterns from start date to today
    const testDates: string[] = [];
    for (let d = new Date(currentDate); d >= startDate; d.setDate(d.getDate() - 1)) {
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = d.getFullYear();
      testDates.push(`${month}${day}${year}.md`);
    }
    
    // Test each file - fetch and check if it's actually markdown content
    // Vite dev server returns index.html for missing routes, so we need to verify content
    const testPromises = testDates.slice(0, 730).map(async filename => {
      try {
        const response = await fetch(`/${filename}`);
        if (!response.ok) {
          return null;
        }
        // Check Content-Type header
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('text/') && !contentType.includes('markdown')) {
          return null;
        }
        // Fetch a small sample to verify it's not HTML
        const text = await response.text();
        // Check if it looks like HTML (starts with <!doctype or <html)
        if (text.trim().toLowerCase().startsWith('<!doctype') || text.trim().toLowerCase().startsWith('<html')) {
          return null;
        }
        // Check if it looks like markdown (has markdown patterns or doesn't look like HTML)
        // If it doesn't start with HTML tags, assume it's markdown
        return filename;
      } catch {
        return null;
      }
    });
    
    const results = await Promise.all(testPromises);
    return results.filter((file): file is string => file !== null);
  };

  // Load a specific patch note file
  const loadPatchNote = (filename: string) => {
    setLoading(true);
    setError(null);
    
    fetch(`/${filename}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load ${filename}`);
        }
        return response.text();
      })
      .then(text => {
        // Verify it's not HTML (Vite dev server might return index.html for missing files)
        if (text.trim().toLowerCase().startsWith('<!doctype') || text.trim().toLowerCase().startsWith('<html')) {
          throw new Error(`File ${filename} appears to be HTML, not markdown`);
        }
        setContent(text);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  // Navigate to next/previous patch note
  const goToNext = () => {
    if (currentIndex < patchNotesFiles.length - 1) {
      const newIndex = currentIndex + 1;
      const filename = patchNotesFiles[newIndex];
      if (filename) {
        setCurrentIndex(newIndex);
        loadPatchNote(filename);
      }
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const filename = patchNotesFiles[newIndex];
      if (filename) {
        setCurrentIndex(newIndex);
        loadPatchNote(filename);
      }
    }
  };

  // Format date from filename for display
  const formatDateFromFilename = (filename: string): string => {
    const date = parseDateFromFilename(filename);
    if (date) {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    return filename.replace('.md', '');
  };

  // Fetch all patch notes files dynamically
  useEffect(() => {
    discoverPatchNotesFiles()
      .then(files => {
        // Sort files by date (newest first)
        const sortedFiles = files
          .map((file: string) => ({
            filename: file,
            date: parseDateFromFilename(file)
          }))
          .filter((item): item is { filename: string; date: Date } => item.date !== null)
          .sort((a, b) => b.date.getTime() - a.date.getTime())
          .map((item) => item.filename);
        
        setPatchNotesFiles(sortedFiles);
        
        // Load the first (newest) file
        if (sortedFiles.length > 0 && sortedFiles[0]) {
          loadPatchNote(sortedFiles[0]);
        } else {
          setLoading(false);
          setError('No patch notes files found');
        }
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return (
    <div className="space-y-8">
      <div 
        className="rounded-xl shadow-sm border p-8 mx-auto"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)',
          width: '75%',
          maxWidth: '75%'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div 
              className="p-2 rounded-lg"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)'
              }}
            >
              <BookOpen className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />
            </div>
            <div>
              <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Patch Notes
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                {patchNotesFiles.length > 0 && currentIndex < patchNotesFiles.length && patchNotesFiles[currentIndex]
                  ? `${formatDateFromFilename(patchNotesFiles[currentIndex])} (${currentIndex + 1} of ${patchNotesFiles.length})`
                  : 'Latest updates, bug fixes, and new features'}
              </p>
            </div>
          </div>
          
          {/* Pagination Controls */}
          {patchNotesFiles.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={goToPrevious}
                disabled={currentIndex === 0 || loading}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: currentIndex === 0 ? 'var(--color-bg-disabled)' : 'var(--color-bg-secondary)',
                  color: currentIndex === 0 ? 'var(--color-text-disabled)' : 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)'
                }}
                onMouseEnter={(e) => {
                  if (currentIndex > 0) {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentIndex > 0) {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }
                }}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={goToNext}
                disabled={currentIndex === patchNotesFiles.length - 1 || loading}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: currentIndex === patchNotesFiles.length - 1 ? 'var(--color-bg-disabled)' : 'var(--color-bg-secondary)',
                  color: currentIndex === patchNotesFiles.length - 1 ? 'var(--color-text-disabled)' : 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)'
                }}
                onMouseEnter={(e) => {
                  if (currentIndex < patchNotesFiles.length - 1) {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentIndex < patchNotesFiles.length - 1) {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }
                }}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
            <p className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading patch notes...</p>
          </div>
        )}
        
        {error && (
          <div className="text-center py-12">
            <p style={{ color: 'var(--color-error)' }}>Error loading patch notes: {error}</p>
          </div>
        )}
        
        {!loading && !error && (
          <div 
            className="max-w-none py-4"
            style={{
              color: 'var(--color-text-primary)',
              lineHeight: '1.7',
              fontSize: '15px'
            }}
          >
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => (
                  <h1 
                    className="text-3xl font-bold mb-6 mt-8 pb-3 border-b" 
                    style={{ 
                      color: 'var(--color-text-primary)',
                      borderColor: 'var(--color-border)'
                    }} 
                    {...props} 
                  />
                ),
                h2: ({node, ...props}) => (
                  <h2 
                    className="text-2xl font-bold mb-4 mt-8 pb-2 border-b" 
                    style={{ 
                      color: 'var(--color-text-primary)',
                      borderColor: 'var(--color-border)'
                    }} 
                    {...props} 
                  />
                ),
                h3: ({node, ...props}) => (
                  <h3 
                    className="text-xl font-semibold mb-3 mt-6" 
                    style={{ 
                      color: 'var(--color-accent)'
                    }} 
                    {...props} 
                  />
                ),
                h4: ({node, ...props}) => (
                  <h4 
                    className="text-lg font-semibold mb-2 mt-4" 
                    style={{ 
                      color: 'var(--color-text-primary)'
                    }} 
                    {...props} 
                  />
                ),
                p: ({node, ...props}) => (
                  <p 
                    className="mb-4 leading-relaxed text-base" 
                    style={{ 
                      color: 'var(--color-text-primary)'
                    }} 
                    {...props} 
                  />
                ),
                ul: ({node, ...props}) => (
                  <ul 
                    className="list-disc mb-4 ml-6 space-y-2" 
                    style={{ 
                      color: 'var(--color-text-primary)'
                    }} 
                    {...props} 
                  />
                ),
                ol: ({node, ...props}) => (
                  <ol 
                    className="list-decimal mb-4 ml-6 space-y-2" 
                    style={{ 
                      color: 'var(--color-text-primary)'
                    }} 
                    {...props} 
                  />
                ),
                li: ({node, ...props}) => (
                  <li 
                    className="mb-1" 
                    style={{ 
                      color: 'var(--color-text-primary)',
                      lineHeight: '1.6'
                    }} 
                    {...props} 
                  />
                ),
                code: ({node, inline, ...props}: any) => inline ? (
                  <code 
                    className="px-1.5 py-0.5 rounded text-sm font-mono" 
                    style={{ 
                      backgroundColor: 'var(--color-bg-tertiary)', 
                      color: 'var(--color-accent)',
                      border: '1px solid var(--color-border)'
                    }} 
                    {...props} 
                  />
                ) : (
                  <code 
                    className="block p-4 rounded-lg mb-4 overflow-x-auto text-sm font-mono" 
                    style={{ 
                      backgroundColor: 'var(--color-bg-tertiary)', 
                      borderColor: 'var(--color-border)', 
                      border: '1px solid',
                      color: 'var(--color-text-primary)'
                    }} 
                    {...props} 
                  />
                ),
                pre: ({node, ...props}) => (
                  <pre 
                    className="p-4 rounded-lg mb-4 overflow-x-auto" 
                    style={{ 
                      backgroundColor: 'var(--color-bg-tertiary)', 
                      borderColor: 'var(--color-border)', 
                      border: '1px solid'
                    }} 
                    {...props} 
                  />
                ),
                blockquote: ({node, ...props}) => (
                  <blockquote 
                    className="border-l-4 pl-4 italic my-4 py-2" 
                    style={{ 
                      borderColor: 'var(--color-accent)', 
                      color: 'var(--color-text-secondary)',
                      backgroundColor: 'var(--color-bg-tertiary)'
                    }} 
                    {...props} 
                  />
                ),
                a: ({node, ...props}) => (
                  <a 
                    className="underline hover:no-underline transition-all" 
                    style={{ 
                      color: 'var(--color-accent)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    {...props} 
                  />
                ),
                hr: ({node, ...props}) => (
                  <hr 
                    className="my-8 border-t-2" 
                    style={{ 
                      borderColor: 'var(--color-border)'
                    }} 
                    {...props} 
                  />
                ),
                strong: ({node, ...props}) => (
                  <strong 
                    className="font-bold" 
                    style={{ 
                      color: 'var(--color-text-primary)'
                    }} 
                    {...props} 
                  />
                ),
                em: ({node, ...props}) => (
                  <em 
                    className="italic" 
                    style={{ 
                      color: 'var(--color-text-primary)'
                    }} 
                    {...props} 
                  />
                )
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};
